import React from "react";
import { Routes, Route } from "react-router-dom";
import AppLayout from "../../dashboard/components/AppLayout.js";
import ErrorBoundary from "../../dashboard/components/ErrorBoundary.js";
import Overview from "../../dashboard/pages/Overview.js";
import Topology from "../../dashboard/pages/Topology.js";
import Clients from "../../dashboard/pages/Clients.js";
import Sites from "../../dashboard/pages/Sites.js";
import Locations from "../../dashboard/pages/Locations.js";
import RoutesPage from "../../dashboard/pages/Routes.js";
import RoutePreview from "../../dashboard/pages/RoutePreview.js";
import Tasks from "../../dashboard/pages/Tasks.js";
import TaskAssignments from "../../dashboard/pages/TaskAssignments.js";
import Submissions from "../../dashboard/pages/Submissions.js";
import SubmissionDetail from "../../dashboard/pages/SubmissionDetail.js";
import Schedules from "../../dashboard/pages/Schedules.js";
import Monitor from "../../dashboard/pages/Monitor.js";
import Reports from "../../dashboard/pages/Reports.js";
import Line from "../../dashboard/pages/Line.js";
import Users from "../../dashboard/pages/Users.js";
import Devices from "../../dashboard/pages/Devices.js";
import Billing from "../../dashboard/pages/Billing.js";
import AiUsage from "../../dashboard/pages/AiUsage.js";
import Audit from "../../dashboard/pages/Audit.js";
import McpApprovals from "../../dashboard/pages/McpApprovals.js";
import McpToolProjections from "../../dashboard/pages/McpToolProjections.js";
import Settings from "../../dashboard/pages/Settings.js";
import MapImportProgress from "../../dashboard/pages/MapImportProgress.js";
import NotFound from "../../dashboard/pages/NotFound.js";
import Forbidden from "../../dashboard/pages/Forbidden.js";
import PlatformLayout from "../../dashboard/pages/platform/PlatformLayout.js";
import PlatformTenants from "../../dashboard/pages/platform/PlatformTenants.js";
import PlatformUsers from "../../dashboard/pages/platform/PlatformUsers.js";
import PlatformHealth from "../../dashboard/pages/platform/PlatformHealth.js";
import PlatformAnnouncements from "../../dashboard/pages/platform/PlatformAnnouncements.js";
import PlatformLinkLine from "../../dashboard/pages/platform/PlatformLinkLine.js";
import PlatformSecrets from "../../dashboard/pages/platform/PlatformSecrets.js";
import PlatformModules from "../../dashboard/pages/platform/PlatformModules.js";
import PlatformRoleTemplates from "../../dashboard/pages/platform/PlatformRoleTemplates.js";
import SharedSkillsList from "../../dashboard/pages/platform/SharedSkillsList.js";
import SharedSkillCandidates from "../../dashboard/pages/platform/SharedSkillCandidates.js";
import SharedSkillCandidateDetail from "../../dashboard/pages/platform/SharedSkillCandidateDetail.js";
import SharedSkillDetail from "../../dashboard/pages/platform/SharedSkillDetail.js";
import BindingCodes from "../../dashboard/pages/admin/BindingCodes.js";
import RolesList from "../../dashboard/pages/admin/RolesList.js";
import RoleCreate from "../../dashboard/pages/admin/RoleCreate.js";
import RoleDetail from "../../dashboard/pages/admin/RoleDetail.js";
import SiteLayoutEditor from "../../dashboard/pages/SiteLayoutEditor.js";
import SiteFloorStack from "../../dashboard/pages/SiteFloorStack.js";
import SiteDetail from "../../dashboard/pages/SiteDetail.js";
import FloorLayoutEditor from "../../dashboard/pages/FloorLayoutEditor.js";
import { DashboardSessionProvider } from "../../dashboard/auth/useDashboardSession.js";
import { RequireRole } from "../../dashboard/auth/RequireRole.js";
import { ConfirmDialogProvider } from "../../dashboard/components/ConfirmDialog.js";

// Module-contributed dashboard routes (G frontend aggregator). One
// import per module; the array is spread into the <Routes> tree
// below. Adding a new module's UI: install its workspace dep + add
// a `[...MODULE_ROUTES, ...ITS_ROUTES]` line here.
import { ROUTES as ASSET_MODULE_ROUTES } from "@nexus/module-asset/frontend/routes";
const MODULE_ROUTES = [...ASSET_MODULE_ROUTES];

// Per-route required permission ids (ANY match grants access). Mirror of
// `Rbac.DASHBOARD_ROUTE_PERMISSIONS` (packages/shared/src/rbac/index.ts)
// — keep in sync. super_admin god-mode bypasses every gate.
// Empty list = any authenticated user passes (e.g. /settings, /forbidden).
const PERMS = {
  // dashboard home — broad union of read perms across roles.
  home:        ["sites:read.list", "patrols:read.self", "patrols:read.list", "schedules:read.self", "incident:read.self", "ai:read.facts", "users:read.list"],
  monitor:     ["sites:read.list", "patrols:read.list"],
  clients:     ["clients:read.list"],
  sites:       ["sites:read.list"],
  locations: ["locations:read.list"],
  routes:      ["routes:read.list"],
  tasks:       ["tasks:read.list"],
  submissions: ["tasks:read.list", "patrols:read.list"],
  schedules:   ["schedules:read.list", "schedules:read.self"],
  reports:     ["reports:read.tenant", "reports:read.site", "reports:read.officer"],
  line:        ["line:execute.bind_group", "line:write.update_group"],
  users:       ["users:read.list"],
  devices:     ["users:read.officer_status"],
  billing:     ["billing:read.invoices", "billing:read.usage"],
  ai_usage:    ["billing:read.usage", "ai:read.usage"],
  audit:       ["audit:read.log"],
  // /dashboard/mcp-approvals — human approver inbox for gated MCP tool-calls.
  // High-risk control-plane action; tenant_admin only (backend re-checks the
  // mcp:approve_tool_call permission regardless — defense-in-depth).
  mcpApprovals:["mcp:approve_tool_call"],
  // /dashboard/mcp-tool-projections — manage per-tenant MCP tool-visibility
  // overrides. High-risk control-plane action; tenant_admin only (backend
  // re-checks mcp:manage_projection on every call — defense-in-depth).
  mcpToolProjections:["mcp:manage_projection"],
  bindingCodes:["users:write.invite"],
  rolesView:   ["roles:read.list"],
  rolesEdit:   ["roles:write.create"],
  // /dashboard/settings — every authenticated user (LINE binding, notif
  // prefs, personal AI memory). Empty list = no permission gate.
  settings:    [] as readonly string[],
  // /dashboard/map-imports/* — floor plan upload pipeline; needs write:site equiv.
  mapImports:  ["floors:write.update", "floors:execute.parse_layout"],
  // /dashboard/platform/* — gated by impersonate permission (super_admin god mode).
  platform:    ["platform:execute.impersonate"],
} as const;

export default function DashboardShell(): React.ReactElement {
  return (
    <DashboardSessionProvider>
      <ConfirmDialogProvider>
      <ErrorBoundary>
        <Routes>
          <Route element={<AppLayout />}>
            <Route index                element={<RequireRole permissions={PERMS.home}><Overview /></RequireRole>} />
            <Route path="topology"      element={<RequireRole permissions={PERMS.home}><Topology /></RequireRole>} />
            <Route path="clients"       element={<RequireRole permissions={PERMS.clients}><Clients /></RequireRole>} />
            <Route path="sites"         element={<RequireRole permissions={PERMS.sites}><Sites /></RequireRole>} />
            <Route path="sites/:siteId" element={<RequireRole permissions={PERMS.sites}><SiteDetail /></RequireRole>} />
            <Route path="sites/:siteId/layout" element={<RequireRole permissions={PERMS.sites}><SiteLayoutEditor /></RequireRole>} />
            <Route path="sites/:siteId/floor-stack" element={<RequireRole permissions={PERMS.sites}><SiteFloorStack /></RequireRole>} />
            {/* Floor-level layout parser + SVG geometry editor (gpt-4o vision). */}
            <Route path="sites/:siteId/buildings/:buildingId/floors/:floorId/layout" element={<RequireRole permissions={PERMS.sites}><FloorLayoutEditor /></RequireRole>} />
            <Route path="locations"   element={<RequireRole permissions={PERMS.locations}><Locations /></RequireRole>} />
            <Route path="routes"        element={<RequireRole permissions={PERMS.routes}><RoutesPage /></RequireRole>} />
            <Route path="routes/:id/preview" element={<RequireRole permissions={PERMS.routes}><RoutePreview /></RequireRole>} />
            <Route path="tasks"         element={<RequireRole permissions={PERMS.tasks}><Tasks /></RequireRole>} />
            <Route path="tasks/:taskId/assignments" element={<RequireRole permissions={PERMS.tasks}><TaskAssignments /></RequireRole>} />
            <Route path="submissions"   element={<RequireRole permissions={PERMS.submissions}><Submissions /></RequireRole>} />
            <Route path="submissions/:id" element={<RequireRole permissions={PERMS.submissions}><SubmissionDetail /></RequireRole>} />
            <Route path="schedules"     element={<RequireRole permissions={PERMS.schedules}><Schedules /></RequireRole>} />
            <Route path="monitor"       element={<RequireRole permissions={PERMS.monitor}><Monitor /></RequireRole>} />
            <Route path="reports"       element={<RequireRole permissions={PERMS.reports}><Reports /></RequireRole>} />
            <Route path="line"          element={<RequireRole permissions={PERMS.line}><Line /></RequireRole>} />
            <Route path="users"         element={<RequireRole permissions={PERMS.users}><Users /></RequireRole>} />
            <Route path="devices"       element={<RequireRole permissions={PERMS.devices}><Devices /></RequireRole>} />
            <Route path="billing"       element={<RequireRole permissions={PERMS.billing}><Billing /></RequireRole>} />
            <Route path="ai-usage"      element={<RequireRole permissions={PERMS.ai_usage}><AiUsage /></RequireRole>} />
            <Route path="audit"         element={<RequireRole permissions={PERMS.audit}><Audit /></RequireRole>} />
            <Route path="mcp-approvals" element={<RequireRole permissions={PERMS.mcpApprovals}><McpApprovals /></RequireRole>} />
            <Route path="mcp-tool-projections" element={<RequireRole permissions={PERMS.mcpToolProjections}><McpToolProjections /></RequireRole>} />
            {/* Binding-codes management — needs users:write.invite */}
            <Route path="admin/binding-codes" element={<RequireRole permissions={PERMS.bindingCodes}><BindingCodes /></RequireRole>} />
            {/* Custom RBAC role management — list/detail = view, /new = edit */}
            <Route path="admin/roles"        element={<RequireRole permissions={PERMS.rolesView}><RolesList /></RequireRole>} />
            <Route path="admin/roles/new"    element={<RequireRole permissions={PERMS.rolesEdit}><RoleCreate /></RequireRole>} />
            <Route path="admin/roles/:id"    element={<RequireRole permissions={PERMS.rolesView}><RoleDetail /></RequireRole>} />

            {/* Self-serve account settings (LINE binding, AI memory, etc) — any user. */}
            <Route path="settings"      element={<RequireRole permissions={PERMS.settings}><Settings /></RequireRole>} />
            {/* Floor plan import pipeline progress page. Upload is
                triggered from the existing FloorLayoutEditor — there's no
                standalone /new page (removed; it duplicated UX and forced
                manual ULID entry). This route is reached only via redirect
                after a successful upload. */}
            <Route path="map-imports/:id"  element={<RequireRole permissions={PERMS.mapImports}><MapImportProgress /></RequireRole>} />

            {/* SENTRY platform admin — super_admin only (god mode owns
                platform:execute.impersonate). */}
            <Route path="platform"
              element={<RequireRole permissions={PERMS.platform}><PlatformLayout /></RequireRole>}
            >
              <Route index                 element={<PlatformTenants />} />
              <Route path="tenants"        element={<PlatformTenants />} />
              <Route path="users"          element={<PlatformUsers />} />
              <Route path="health"         element={<PlatformHealth />} />
              <Route path="announcements"  element={<PlatformAnnouncements />} />
              <Route path="me/link-line"   element={<PlatformLinkLine />} />
              <Route path="secrets"        element={<PlatformSecrets />} />
              <Route path="modules"        element={<PlatformModules />} />
              <Route path="role-templates" element={<PlatformRoleTemplates />} />
              <Route path="shared-skills"                       element={<SharedSkillsList />} />
              <Route path="shared-skills/candidates"            element={<SharedSkillCandidates />} />
              <Route path="shared-skills/candidates/:id"        element={<SharedSkillCandidateDetail />} />
              <Route path="shared-skills/:id"                   element={<SharedSkillDetail />} />
            </Route>
            {/* ── Module-contributed routes ──────────────────────
                Modules ship their own pages via
                modules/<id>/frontend/routes.tsx; the host SPA spreads
                them into the dashboard router here. Each ROUTES
                entry carries its own permission set so a module
                page never bypasses RequireRole. Adding a new
                module → import its ROUTES + add another spread
                here. Future runtime aggregator will fold these
                imports into a single iteration over module manifests. */}
            {MODULE_ROUTES.map((r) => (
              <Route
                key={r.path}
                path={r.path}
                element={
                  r.permissions.length > 0 ? (
                    <RequireRole permissions={r.permissions}>{r.element}</RequireRole>
                  ) : (
                    r.element
                  )
                }
              />
            ))}

            {/* Forbidden landing — always reachable so RequireRole can redirect here */}
            <Route path="forbidden"     element={<Forbidden />} />
            <Route path="*"             element={<NotFound />} />
          </Route>
        </Routes>
      </ErrorBoundary>
      </ConfirmDialogProvider>
    </DashboardSessionProvider>
  );
}
